import { useState } from 'react';
import {
  dmMono,
  mono,
  labelStyle,
  textAreaStyle,
  sectionHeaderStyle,
  secondaryButtonStyle,
  primaryButtonStyle,
} from './tokens';

const AUTONOMY_LEVELS = [
  { id: 0, label: 'Manual', color: '#666', description: 'Only acts when told.' },
  { id: 1, label: 'Reactive', color: '#53b1fd', description: 'Runs assigned tasks, returns to life sim.' },
  { id: 2, label: 'Proactive', color: '#fbbf24', description: 'Thinks periodically, self-assigns tasks from mission.' },
  { id: 3, label: 'Autonomous', color: '#32d583', description: 'Continuous self-direction with daily summaries.' },
];

function initialForm(agent) {
  const directives = agent?.directives || {};
  return {
    autonomyLevel:
      typeof agent?.autonomyLevel === 'number' ? agent.autonomyLevel : 0,
    mission: directives.mission || '',
    behavioral: directives.behavioral || '',
    constraints: directives.constraints || '',
  };
}

function SummaryRow({ label, value, placeholder }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ ...labelStyle, marginBottom: 3 }}>{label}</div>
      <div
        style={{
          ...dmMono,
          fontSize: 11,
          color: value ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.25)',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {value || placeholder}
      </div>
    </div>
  );
}

function DirectivesSectionContent({
  agent,
  color = '#53b1fd',
  saving = false,
  saveError = null,
  onSave,
}) {
  const [mode, setMode] = useState('view');
  const [form, setForm] = useState(() => initialForm(agent));

  const autonomy =
    AUTONOMY_LEVELS.find((l) => l.id === form.autonomyLevel) || AUTONOMY_LEVELS[0];

  const handleSave = async () => {
    if (saving) return;
    await onSave?.({
      autonomyLevel: form.autonomyLevel,
      directives: {
        mission: form.mission.trim(),
        behavioral: form.behavioral.trim(),
        constraints: form.constraints.trim(),
      },
    });
    setMode('view');
  };

  const handleCancel = () => {
    setForm(initialForm(agent));
    setMode('view');
  };

  const isEditing = mode === 'edit';
  const directives = agent?.directives || {};
  const committedAutonomy =
    AUTONOMY_LEVELS.find(
      (l) => l.id === (typeof agent?.autonomyLevel === 'number' ? agent.autonomyLevel : 0),
    ) || AUTONOMY_LEVELS[0];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div style={sectionHeaderStyle}>Directives</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {isEditing ? (
            <>
              <button onClick={handleCancel} style={secondaryButtonStyle}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  ...primaryButtonStyle,
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'progress' : 'pointer',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button onClick={() => setMode('edit')} style={secondaryButtonStyle}>
              Edit
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div
          style={{
            display: 'grid',
            gap: 12,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${color}33`,
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div>
            <div style={{ ...labelStyle, marginBottom: 6 }}>Autonomy Level</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="range"
                min={0}
                max={3}
                step={1}
                value={form.autonomyLevel}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, autonomyLevel: +e.target.value }))
                }
                style={{ flex: 1, accentColor: autonomy.color }}
              />
              <span
                style={{
                  ...mono,
                  fontSize: 12,
                  fontWeight: 600,
                  color: autonomy.color,
                  minWidth: 90,
                }}
              >
                {autonomy.label}
              </span>
            </div>
            <div
              style={{
                ...mono,
                fontSize: 9,
                color: 'rgba(255,255,255,0.4)',
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              {autonomy.description}
            </div>
          </div>

          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>Mission</span>
            <textarea
              value={form.mission}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, mission: e.target.value }))
              }
              rows={2}
              placeholder="Primary purpose and goals..."
              style={textAreaStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>Behavioral</span>
            <textarea
              value={form.behavioral}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, behavioral: e.target.value }))
              }
              rows={2}
              placeholder="How to approach work..."
              style={textAreaStyle}
            />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={labelStyle}>Constraints</span>
            <textarea
              value={form.constraints}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, constraints: e.target.value }))
              }
              rows={2}
              placeholder="What NOT to do..."
              style={textAreaStyle}
            />
          </label>

          {saveError ? (
            <div
              style={{
                ...mono,
                fontSize: 10,
                color: '#fca5a5',
                padding: '8px 10px',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 6,
                background: 'rgba(248,113,113,0.08)',
              }}
            >
              {saveError}
            </div>
          ) : null}
        </div>
      ) : (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <div style={{ ...labelStyle, marginBottom: 3 }}>Autonomy</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                ...mono,
                fontSize: 12,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: committedAutonomy.color,
                  boxShadow: `0 0 8px ${committedAutonomy.color}99`,
                }}
              />
              <span style={{ color: committedAutonomy.color, fontWeight: 600 }}>
                {committedAutonomy.label}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                · {committedAutonomy.description}
              </span>
            </div>
          </div>
          <SummaryRow
            label="Mission"
            value={directives.mission}
            placeholder="Not set"
          />
          <SummaryRow
            label="Behavioral"
            value={directives.behavioral}
            placeholder="Not set"
          />
          <SummaryRow
            label="Constraints"
            value={directives.constraints}
            placeholder="Not set"
          />
        </div>
      )}
    </div>
  );
}

export default function DirectivesSection(props) {
  return <DirectivesSectionContent key={props.agent?.id ?? 'new-agent'} {...props} />;
}
